import { ImageBackground, View, Platform, type ImageSourcePropType } from 'react-native';
import Svg, { G, Path, Pattern, Rect, Circle, Defs, Ellipse } from 'react-native-svg';

import patternCrema from '@assets/brand/pattern-crema.png';
import patternMagenta from '@assets/brand/pattern-magenta.png';

interface Props {
  color?: string;
  opacity?: number;
  size?: number;
}

/**
 * Patrón de huellitas (almohadillas) — usado en cards/headers magenta oscuros.
 */
export function PawPattern({ color = '#FFFFFF', opacity = 0.08, size = 56 }: Props) {
  return (
    <View
      pointerEvents="none"
      style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <Svg width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
        <Defs>
          <Pattern id="paws" x="0" y="0" width={size} height={size} patternUnits="userSpaceOnUse">
            <G fill={color} opacity={opacity}>
              <Path d="M16 30c-2 0-3.5-1.7-3.5-3.7s1.5-3.6 3.5-3.6 3.5 1.6 3.5 3.6S18 30 16 30zm-6.4-7.5c-1.6 0-2.7-1.3-2.7-2.9s1.1-2.9 2.7-2.9 2.7 1.3 2.7 2.9-1.1 2.9-2.7 2.9zm12.8 0c-1.6 0-2.7-1.3-2.7-2.9s1.1-2.9 2.7-2.9 2.7 1.3 2.7 2.9-1.1 2.9-2.7 2.9zm-9 .8c-1.4 0-2.4-1.1-2.4-2.5s1-2.5 2.4-2.5 2.4 1.1 2.4 2.5-1 2.5-2.4 2.5zm5.2 0c-1.4 0-2.4-1.1-2.4-2.5s1-2.5 2.4-2.5 2.4 1.1 2.4 2.5-1 2.5-2.4 2.5z" />
              <G transform={`translate(${size / 2}, ${size / 2}) rotate(25)`}>
                <Path d="M0 7c-1.4 0-2.5-1.2-2.5-2.7s1.1-2.6 2.5-2.6 2.5 1.1 2.5 2.6S1.4 7 0 7zm-4.5-5.4c-1.1 0-1.9-1-1.9-2.1s.8-2.1 1.9-2.1 1.9.9 1.9 2.1-.8 2.1-1.9 2.1zm9 0c-1.1 0-1.9-1-1.9-2.1s.8-2.1 1.9-2.1 1.9.9 1.9 2.1-.8 2.1-1.9 2.1zm-6.5.6C-3.1 2.2-3.8 1.4-3.8.4S-3.1-1.4-2-1.4s1.7.8 1.7 1.8S-1 2.2-2 2.2zm3.7 0c-1 0-1.7-.8-1.7-1.8S1 -1.4 2-1.4s1.7.8 1.7 1.8S2.7 2.2 1.7 2.2z" />
              </G>
            </G>
          </Pattern>
        </Defs>
        <Rect width="100%" height="100%" fill="url(#paws)" />
      </Svg>
    </View>
  );
}

/**
 * Patrón de gatos + pescaditos + ovillos — usa las imágenes oficiales del branding CliniCAT.
 *
 * Variantes:
 *  - "magenta": patrón sobre fondo magenta (ideal para áreas oscuras / heros)
 *  - "crema":   patrón sobre fondo crema (ideal para áreas claras / background general)
 */
export function CatFishPattern({
  variant = 'crema',
  opacity = 0.55,
  tileSize = 360,
}: {
  variant?: 'magenta' | 'crema';
  opacity?: number;
  tileSize?: number;
}) {
  const source: ImageSourcePropType = variant === 'magenta' ? patternMagenta : patternCrema;

  if (Platform.OS === 'web') {
    // En web aprovechamos `background-repeat` con la URL del asset.
    const url = typeof source === 'string' ? source : (source as any)?.uri ?? (source as any)?.default ?? source;
    return (
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity,
          // @ts-ignore web only
          backgroundImage: `url(${url})`,
          // @ts-ignore web only
          backgroundRepeat: 'repeat',
          // @ts-ignore web only
          backgroundSize: `${tileSize}px auto`,
        }}
      />
    );
  }

  return (
    <View pointerEvents="none" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity }}>
      <ImageBackground
        source={source}
        resizeMode="repeat"
        style={{ width: '100%', height: '100%' }}
      />
    </View>
  );
}

/**
 * Huella grande de acento.
 */
export function PawAccent({ size = 48, color = '#FFFFFF', opacity = 0.18 }: { size?: number; color?: string; opacity?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 48 48" opacity={opacity}>
      <G fill={color}>
        <Circle cx={14} cy={16} r={4.5} />
        <Circle cx={24} cy={11} r={4.5} />
        <Circle cx={34} cy={16} r={4.5} />
        <Circle cx={9} cy={26} r={3.5} />
        <Circle cx={39} cy={26} r={3.5} />
        <Path d="M24 22c-7 0-12 5-12 11 0 4 3 7 7 7 2 0 3-1 5-1s3 1 5 1c4 0 7-3 7-7 0-6-5-11-12-11z" />
      </G>
    </Svg>
  );
}
